import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';
import { type IsSessionActiveQuery } from '../../__generated__/graphql-client-types';
import { AUTH_REDIRECT_REASON } from '../../constants/general';
import { ACCOUNT_LOGIN_LINK } from '../../constants/links';
import { IS_SESSION_ACTIVE } from '../../queries/authentication/authentication.queries';
import { useNavigation } from '../navigation/navigation.hooks';

type UseActiveSessionRedirectOptions = {
	enablePolling?: boolean;
	redirectOnExpiry?: boolean;
	redirectRoute?: string;
	pollInterval?: number;
	returnToRoute?: string;
};

type UseActiveSessionRedirectResult = {
	isSessionActive?: boolean;
	isLoading: boolean;
};

export const useActiveSessionRedirect = (options?: UseActiveSessionRedirectOptions): UseActiveSessionRedirectResult => {
	const {
		enablePolling = true,
		redirectOnExpiry = true,
		redirectRoute = ACCOUNT_LOGIN_LINK,
		pollInterval = 30000, // 30 seconds in ms
		returnToRoute
	} = options || {};

	const navigate = useNavigation();

	const { data, loading } = useQuery<IsSessionActiveQuery>(IS_SESSION_ACTIVE, {
		pollInterval: enablePolling ? pollInterval : undefined,
		fetchPolicy: 'network-only',
		skip: !enablePolling
	});

	const isSessionActive = data?.isSessionActive;

	let returnTo: string;
	if (returnToRoute) {
		returnTo = `&returnto=${returnToRoute}`;
	} else {
		returnTo = '';
	}

	useEffect(() => {
		if (!loading && redirectOnExpiry && isSessionActive === false) {
			navigate(`${redirectRoute}?error=${AUTH_REDIRECT_REASON.SESSION_EXPIRED}${returnTo}`);
		}
	}, [isSessionActive, loading, navigate, redirectOnExpiry, redirectRoute, returnTo]);

	return {
		isSessionActive,
		isLoading: loading
	};
};
